import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { ICON, Notice, Skeleton } from '../components/ui';
import {
  STEPS,
  collectProfilePatch,
  validateStep,
  type OnboardingData,
  type StepKey,
} from '../components/onboarding/shared';
import {
  Step1Welcome,
  Step2Naw,
  Step3Contact,
  Step4Sensitive,
  Step5Werkgever,
  Step6Contract,
  Step7Objects,
  Step8Diplomas,
  Step9Salary,
  Step10Iban,
  Step11Preferences,
  Step12Summary,
} from '../components/onboarding/Steps';

export function OnboardingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ['onboarding'],
    queryFn: api.onboarding.progress,
  });
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: api.profile.get,
  });

  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bannerError, setBannerError] = useState<string | null>(null);
  const resumed = useRef(false);

  // Resume: zodra we de eerste keer progress + profile hebben, springen we direct
  // naar de laatste actieve stap zonder state te overschrijven bij latere refetches.
  useEffect(() => {
    if (resumed.current) return;
    if (!progress) return;
    if (progress.is_completed) {
      navigate('/', { replace: true });
      return;
    }
    setStep(progress.current_step || 1);
    if (progress.progress_data) setData(progress.progress_data as OnboardingData);
    resumed.current = true;
  }, [progress, navigate]);

  const saveProgress = useMutation({
    mutationFn: (payload: { current_step: number; progress_data: OnboardingData }) =>
      api.onboarding.updateProgress(payload),
  });

  const updateProfile = useMutation({
    mutationFn: (payload: any) => api.profile.patch(payload),
  });

  const completeMut = useMutation({
    mutationFn: () => api.onboarding.complete(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onboarding'] });
      qc.invalidateQueries({ queryKey: ['profile'] });
      navigate('/', { replace: true });
    },
  });

  const currentStep = STEPS[step - 1];
  const isLast = step === STEPS.length;

  const persistData = (next: OnboardingData) => {
    setData(next);
    saveProgress.mutate({ current_step: step, progress_data: next });
  };

  const setSection = <K extends keyof OnboardingData>(section: K, patch: OnboardingData[K]) => {
    persistData({ ...data, [section]: patch });
    // Clear errors that may now be resolved
    setErrors({});
  };

  const goToStep = (target: number) => {
    const clamped = Math.max(1, Math.min(STEPS.length, target));
    setStep(clamped);
    setErrors({});
    setBannerError(null);
    saveProgress.mutate({ current_step: clamped, progress_data: data });
  };

  const goNext = async () => {
    setBannerError(null);
    const validation = validateStep(currentStep.key, data);
    if (!validation.ok) {
      setErrors(validation.errors);
      setBannerError('Vul de gemarkeerde velden goed in voordat je verder gaat.');
      return;
    }
    setErrors({});
    // Persist profile-relevant patches naar D1 elke keer dat een schoongeschreven sectie passes
    const patch = collectSectionPatch(currentStep.key, data);
    if (Object.keys(patch).length > 0) {
      try {
        await updateProfile.mutateAsync(patch);
      } catch (err) {
        setBannerError(`Opslaan mislukt: ${(err as Error).message}`);
        return;
      }
    }
    if (isLast) {
      try {
        const fullPatch = collectProfilePatch(data);
        if (Object.keys(fullPatch).length > 0) {
          await updateProfile.mutateAsync(fullPatch);
        }
        await completeMut.mutateAsync();
      } catch (err) {
        setBannerError(`Voltooien mislukt: ${(err as Error).message}`);
      }
      return;
    }
    goToStep(step + 1);
  };

  const goBack = () => goToStep(step - 1);

  const stepLoading = progressLoading || profileLoading;

  const stepProps = useMemo(() => ({ data, profile, errors }), [data, profile, errors]);

  const pct = Math.round((step / STEPS.length) * 100);

  return (
    <div className="min-h-screen px-4 pb-28 pt-8 sm:flex sm:items-center sm:justify-center sm:pb-10">
      <div className="mx-auto w-full max-w-2xl">
        {/* Stapkop met voortgang */}
        <header className="mb-4 sm:mb-6">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="t-label t-num">
              Stap {step} van {STEPS.length}
            </span>
            {saveProgress.isPending && <span className="status-muted">Opslaan…</span>}
          </div>
          <div
            className="h-1 overflow-hidden rounded-full bg-slate-700/60"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
            aria-valuenow={step}
            aria-label={`Stap ${step} van ${STEPS.length}: ${currentStep.title}`}
          >
            <div className="h-full rounded-full bg-brand-400 transition-[width] duration-slow ease-calm" style={{ width: `${pct}%` }} />
          </div>
          <h1 className="mt-5">{currentStep.title}</h1>
          {currentStep.subtitle && <p className="page-sub">{currentStep.subtitle}</p>}
        </header>

        <div className="card enter space-y-4">
          {bannerError && <Notice tone="err">{bannerError}</Notice>}

          {stepLoading ? (
            <div className="space-y-3" aria-busy="true" aria-label="Laden">
              <Skeleton className="h-4 w-40" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            </div>
          ) : (
            <StepRender
              key={currentStep.key}
              stepKey={currentStep.key}
              {...stepProps}
              onSetSection={setSection}
              onJumpToStep={goToStep}
            />
          )}
        </div>

        {/* Knoppen: vast onderaan op mobiel, onder de kaart op desktop */}
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-surface-line bg-surface px-4 py-3 sm:static sm:mt-4 sm:border-0 sm:bg-transparent sm:p-0"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto flex max-w-2xl items-center gap-3">
            <button onClick={goBack} disabled={step === 1} className="btn-ghost">
              <ChevronLeft className={ICON.sm} aria-hidden /> Vorige
            </button>
            <span className="hidden flex-1 text-center text-xs text-slate-500 sm:block">Je voortgang wordt bewaard</span>
            <span className="flex-1 sm:hidden" />
            {!isLast ? (
              <button onClick={goNext} className="btn-primary" disabled={updateProfile.isPending}>
                {updateProfile.isPending ? 'Opslaan…' : 'Volgende'}
                {!updateProfile.isPending && <ChevronRight className={ICON.sm} aria-hidden />}
              </button>
            ) : (
              <button onClick={goNext} className="btn-primary" disabled={completeMut.isPending || updateProfile.isPending}>
                {completeMut.isPending ? 'Bezig…' : 'Naar de app'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface RenderProps {
  stepKey: StepKey;
  data: OnboardingData;
  profile: any;
  errors: Record<string, string>;
  onSetSection: <K extends keyof OnboardingData>(section: K, patch: OnboardingData[K]) => void;
  onJumpToStep: (step: number) => void;
}

function StepRender({ stepKey, data, profile, errors, onSetSection, onJumpToStep }: RenderProps) {
  switch (stepKey) {
    case 'welcome':
      return <Step1Welcome profile={profile} />;
    case 'naw':
      return <Step2Naw data={data} profile={profile} errors={errors} onChange={(patch) => onSetSection('naw', patch)} />;
    case 'contact':
      return <Step3Contact data={data} profile={profile} errors={errors} onChange={(patch) => onSetSection('contact', patch)} />;
    case 'sensitive':
      return <Step4Sensitive data={data} profile={profile} errors={errors} onChange={(patch) => onSetSection('sensitive', patch)} />;
    case 'werkgever':
      return <Step5Werkgever data={data} profile={profile} errors={errors} onChange={(patch) => onSetSection('werkgever', patch)} />;
    case 'contract':
      return <Step6Contract />;
    case 'objects':
      return <Step7Objects />;
    case 'diplomas':
      return <Step8Diplomas />;
    case 'salary':
      return <Step9Salary />;
    case 'iban':
      return <Step10Iban data={data} profile={profile} errors={errors} onChange={(patch) => onSetSection('iban', patch)} />;
    case 'preferences':
      return <Step11Preferences />;
    case 'summary':
      return (
        <Step12Summary
          data={data}
          profile={profile}
          onJumpToStep={onJumpToStep}
          onEdit={(section, patch) => onSetSection(section, patch)}
        />
      );
  }
}

/** Pakt alleen de velden uit `data[section]` die bij deze stap horen voor PATCH /api/profile. */
function collectSectionPatch(stepKey: StepKey, data: OnboardingData): Record<string, any> {
  const sectionMap: Partial<Record<StepKey, keyof OnboardingData>> = {
    naw: 'naw',
    contact: 'contact',
    sensitive: 'sensitive',
    werkgever: 'werkgever',
    iban: 'iban',
  };
  const sec = sectionMap[stepKey];
  if (!sec) return {};
  const src = data[sec] ?? {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(src)) {
    if (v === undefined) continue;
    out[k] = v === '' ? null : v;
  }
  return out;
}
